import React, { useEffect, useState } from "react";
import style from "./index.module.scss";
import { Button, Col, Icon, message, Row, Spin, Switch } from "antd";
import useRequest from "../../../../utils/useRequest";
import {
    ExtraType,
    getData,
    queryRule,
    RuleModelItem,
    RuleParams,
    saveFilePath,
    saveRule
} from "../../../../service/score";
import TableHeadTip from "../components/TableHeadTip";
import TableBody from "../components/TableBody";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage, getExtName } from "service/service";
import useRequestFn from "utils/useRequestFn";
import Form from "rc-field-form";
import { useAtomValue, useSetAtom } from "jotai";
import { isRequiredAtom } from "../components/TableBody/store";
import { disabledAtom, isProcessAtom } from "../store";
import RuleText from "../components/RuleText";
import ResetBtn from "../components/ResetBtn";
import { isTrue } from "service/service";
import SwitchRender from "../components/SwitchRender";
import SaveTools from "../components/SaveTools";
import { ChainUploadFileProps } from "../../../../components/ChainUpload/servers";
import ChainUpload from "../../../../components/ChainUpload";
import config from "../../../../config";
import ReactDOM from "react-dom";
import FileItem from "../../../../components/FileItem";
import ScoreContext from "./context";
import AddSc from "./Components/AddSc";
import useModalHandler from "utils/v3/useModalHandler";
import CopyModal from "./Components/CopyModal";
import { isNull } from "lodash-es";

interface RuleSettingProps {
    courseNo: string;
    courseSeq: string;
}

const Index = ({ courseNo, courseSeq }: RuleSettingProps) => {
    const [form] = Form.useForm();
    const okModal = useModalHandler();

    const disabled = useAtomValue(disabledAtom({ courseNo, courseSeq }));

    const isRequired = useAtomValue(isRequiredAtom);

    const [prevData, setPrevData] = useState<RuleParams | null>(null);

    const [save, { loading }] = useRequestFn(saveRule);
    const [data, { loading: dataLoading }, updateData] = useRequest(
        () => queryRule(courseNo, courseSeq),
        {},
        [courseNo, courseSeq]
    );

    const [gradeWeight, setGradeWeight] = useState({
        dailyGrade: data?.dailyGrade || 0,
        finalGrade: data?.finalGrade || 0
    }); //成绩占比
    const [isProcess, setIsProcess] = useState<BooleanValue>("0"); //过程化考核
    const [visible, setVisible] = useState<BooleanValue>("0"); //是否可见
    const [description, setDescription] = useState("");
    const updateIsProcess = useSetAtom(isProcessAtom);

    const [dailyGradeRuleModel, setDailyGradeRuleModel] = useState<
        RuleModelItem[]
    >(data?.dailyGradeRuleModel || []); //平时成绩

    const [finalGradeRuleModel, setFinalGradeRuleModel] = useState<
        RuleModelItem[]
    >(data?.finalGradeRuleModel || []); //期末成绩

    const [extraRule, setExtraRule] = useState<ExtraType>(null);

    const [settingTitle, setSettingTitle] = useState("确认保存成绩规则？");

    useEffect(() => {
        if (!data) return;
        ReactDOM.unstable_batchedUpdates(() => {
            setDailyGradeRuleModel(data.dailyGradeRuleModel);
            setFinalGradeRuleModel(data.finalGradeRuleModel);
            setGradeWeight({
                dailyGrade: data.dailyGrade,
                finalGrade: data.finalGrade
            });
            setIsProcess(data.isProcess);
            setVisible(data.visible);
            setDescription(data.description);
            setExtraRule(data.extraRule);
            setCurrentFile({
                url: data.filePath,
                name: data.fileName,
                type: data.fileType
            });
            if (data.extraRule) {
                expChange(true);
            }
            updateIsProcess(data.isProcess);
        });
    }, [data]);

    const [saveFileAction, { loading: saveFileLoading }] =
        useRequestFn(saveFilePath);
    const [currentFile, setCurrentFile] = useState({
        url: "",
        name: "",
        type: ""
    });

    function onUploadSuccess(Files: ChainUploadFileProps[]) {
        const files = Files.filter(item => item.status === "done")[0];
        setCurrentFile({
            name: files.file.name,
            type: getExtName(files.file.name)!,
            url: files.response?.key!
        });
    }

    function onPrev() {
        if (!data) return;
        setPrevData({
            ...data,
            description,
            filePath: currentFile.url,
            isProcess: isProcess,
            visible: visible,
            dailyGrade: gradeWeight.dailyGrade,
            finalGrade: gradeWeight.finalGrade,
            dailyGradeRuleModel: getData(dailyGradeRuleModel),
            finalGradeRuleModel: getData(finalGradeRuleModel),
            extraRule
        });
    }

    const onSave = async () => {
        if (!data) return;
        if (expSwitch && !extraRule) {
            return message.warning("必须选择一个额外加分项目");
        }

        try {
            await save({
                ...data,
                description,
                isProcess: isProcess,
                visible: visible,
                filePath: currentFile.url,
                fileType: currentFile.type,
                fileName: currentFile.name,
                dailyGrade: gradeWeight.dailyGrade,
                finalGrade: gradeWeight.finalGrade,
                dailyGradeRuleModel: getData(dailyGradeRuleModel),
                finalGradeRuleModel: getData(finalGradeRuleModel),
                extraRule
            });
            await saveFileAction({
                courseNo,
                courseSeq,
                fileName: currentFile.name,
                filePath: currentFile.url,
                fileType: currentFile.type
            });

            message.success("保存成功");
            updateIsProcess(isProcess);
            updateData();
            okModal.close();
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };

    const onOk = () => {
        if (isRequired) {
            message.error("有必填项未输入");
            return;
        }

        onChangeTitle("确认保存成绩规则?");
    };

    const onChangeTitle = (text: string) => {
        okModal.open();
        setSettingTitle(text);
    };

    const [expSwitch, expChange] = useState(false);
    useEffect(() => {
        if (expSwitch) {
            setExtraRule(extraRule);
        } else {
            setExtraRule(null);
        }
    }, [expSwitch]);

    const copyModal = useModalHandler({
        component: CopyModal
    });

    return (
        <ScoreContext.Provider
            value={{
                prevData
            }}>
            <div className={style.root}>
                <Form form={form} component={false}>
                    <div className={style.tips}>
                        <div className={style.tipLeft}>
                            规则设置说明：
                            <br />
                            1.总成绩由平时成绩和期末成绩组成，具体考核项名称及权重可修改，也可以自定义考核项作为补充考核标准。成绩规则支持复制应用到本人授课的其他课程。
                            <br />
                            2.成绩规则设置打开
                            <b className={style.care}>允许学生查看成绩规则</b>
                            开关，学生端才能查看到教师发布的成绩规则。
                        </div>
                        <div>
                            {!!data?.id && (
                                <>
                                    {!disabled && (
                                        <ResetBtn
                                            courseNo={courseNo}
                                            courseSeq={courseSeq}
                                            updateData={updateData}
                                        />
                                    )}

                                    <Button
                                        type="link"
                                        onClick={() => onChangeTitle("设置")}>
                                        <Icon
                                            type="setting"
                                            className={style.setting}
                                        />
                                        设置
                                    </Button>
                                </>
                            )}
                            <Button
                                disabled={!isNull(data?.id)}
                                type="link"
                                onClick={() => {
                                    copyModal.open({
                                        courseSeq,
                                        courseNo,
                                        onSuccess: () => {
                                            updateData();
                                            copyModal.close();
                                        }
                                    });
                                }}>
                                <Icon type="copy" />
                                复制成绩规则
                            </Button>
                        </div>
                    </div>
                    <RuleText
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                    <Row
                        style={{ marginBottom: 15 }}
                        gutter={15}
                        type="flex"
                        align="middle">
                        <Col>
                            <Spin spinning={saveFileLoading}>
                                <ChainUpload
                                    buttonText="点击上传附件（仅可上传pdf文件）"
                                    multiple={false}
                                    onCancel={() => {}}
                                    onUploadSuccess={onUploadSuccess}
                                    tokenUrl="/teacher/getUpToken"
                                    uploadUrl="https://up-z1.qiniup.com"
                                />
                            </Spin>
                        </Col>
                        {currentFile?.url?.length > 0 && (
                            <>
                                <Col>
                                    <a
                                        href={`${config.QiNiuDownloadUrl}/${currentFile.url}?attname=${currentFile.name}`}
                                        target="_blank"
                                        className={style.label}>
                                        <FileItem
                                            text={currentFile.name}
                                            fileType={currentFile.type}
                                        />
                                    </a>
                                </Col>
                                <Col>
                                    <Icon
                                        type="delete"
                                        onClick={() =>
                                            setCurrentFile({
                                                name: "",
                                                type: "",
                                                url: ""
                                            })
                                        }
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                    <Spin spinning={dataLoading}>
                        <div className={style.table}>
                            <TableHeadTip
                                title="平时成绩"
                                percent={gradeWeight.dailyGrade || 0}
                                onChangePercent={(val: number) =>
                                    setGradeWeight({
                                        ...gradeWeight,
                                        dailyGrade: val
                                    })
                                }
                            />
                            <TableBody
                                data={dailyGradeRuleModel}
                                handleData={setDailyGradeRuleModel}
                                type="0"
                                percent={gradeWeight.dailyGrade}
                                ruleCourseName="平时成绩"
                            />
                        </div>
                        <div className={style.table} style={{ marginTop: 20 }}>
                            <TableHeadTip
                                title="期末成绩"
                                percent={gradeWeight.finalGrade || 0}
                                onChangePercent={(val: number) =>
                                    setGradeWeight({
                                        ...gradeWeight,
                                        finalGrade: val
                                    })
                                }
                            />
                            <TableBody
                                data={finalGradeRuleModel}
                                handleData={setFinalGradeRuleModel}
                                type="1"
                                percent={gradeWeight.finalGrade}
                                ruleCourseName="期末成绩"
                            />
                        </div>
                        <div className={style.table} style={{ marginTop: 20 }}>
                            <TableHeadTip
                                title="额外加分"
                                exp={
                                    <Switch
                                        disabled={disabled}
                                        checked={expSwitch}
                                        onChange={expChange}
                                    />
                                }
                                isShowTips={false}
                                help="额外加分加入最终成绩，为鼓励学生完成规定外的学习任务或对突出表现的嘉奖设置的加分项目，不参与权量折算."
                            />
                            <AddSc
                                disabled={disabled}
                                extraRule={extraRule}
                                expSwitch={expSwitch}
                                setExtraRule={setExtraRule}
                            />
                        </div>
                    </Spin>

                    <SaveTools
                        onPrev={onPrev}
                        gradeWeight={gradeWeight}
                        onOk={onOk}
                        loading={loading}
                        id={data?.id}
                        courseNo={courseNo}
                        courseSeq={courseSeq}
                    />

                    {okModal.render(
                        <SwitchRender
                            visible={okModal.visible}
                            onCancel={okModal.close}
                            isProcess={isTrue(isProcess)}
                            isVisible={isTrue(visible)}
                            setIsProcess={setIsProcess}
                            setVisible={setVisible}
                            onSave={() => onSave()}
                            title={settingTitle}
                            confirmLoading={loading}
                        />
                    )}

                    {copyModal.render()}
                </Form>
            </div>
        </ScoreContext.Provider>
    );
};

export default Index;
